import { Injectable, inject, EnvironmentInjector, runInInjectionContext } from '@angular/core';
import { Firestore, doc, getDoc, collection, getDocs, updateDoc } from '@angular/fire/firestore';
import { Functions } from '@angular/fire/functions';
import { httpsCallable } from './telemetry';
import { Observable, from, map, of, shareReplay, catchError } from 'rxjs';
import {
    marketForCountry,
    isServiceBillable,
    pickEffective,
    pickUpcoming
} from '../whatsapp/service-billing.generated';

export interface WhatsAppRate {
    /** Human label, e.g. "South Africa" or "Rest of Africa". */
    country: string;
    /**
     * Market code — NOT always a country code.
     *
     * Meta prices by market: roughly forty countries have their own row and use
     * their ISO alpha-2 code, while every other country resolves through a
     * region (`REST_OF_AFRICA`, `NORTH_AMERICA`, `OTHER`, …). Resolve an
     * organisation's row with `resolveOrgRate`, never by comparing this to a
     * country code directly.
     */
    code: string;
    marketing: number;
    utility: number;
    authentication: number;
    authenticationInternational: number;
    service: number;
}

/** One dated revision of a rate card. */
export interface WhatsAppRateRevision {
    rateCardVersion: string;
    effectiveFromMs: number;
    rates: WhatsAppRate[];
}

export interface WhatsAppPricingDocument {
    currency: string;
    currencySymbol: string;
    /**
     * The rates in force right now.
     *
     * Derived from `revisions` on read, so a card whose next revision is already
     * seeded switches over on its effective date with no deploy. Documents
     * seeded before revisions existed carry only this field, and are read as a
     * single revision already in force.
     */
    rates: WhatsAppRate[];
    /** Every known revision, ascending. Empty for a legacy document. */
    revisions?: WhatsAppRateRevision[];
    /** Label of the revision `rates` came from. */
    rateCardVersion?: string;
    effectiveFromMs?: number;
    /** The revision that takes over next, when one is seeded. */
    upcoming?: WhatsAppRateRevision | null;
    updatedAt: any;
    updatedBy: string;
}

export interface MessagingLimitConfig {
    marketing: { limit: number | null; action: 'none' | 'notify' | 'block' };
    utility: { limit: number | null; action: 'none' | 'notify' | 'block' };
    authentication: { limit: number | null; action: 'none' | 'notify' | 'block' };
    service: { limit: number | null; action: 'none' | 'notify' | 'block' };
}

export type MessageCategory = 'marketing' | 'utility' | 'authentication' | 'service';

/** Per-category limit state, as returned by the cost summary. */
export interface CategoryLimitState {
    limit: number | null;
    action: 'none' | 'notify' | 'block';
    /** Confirmed billed by Meta this month. */
    counted: number;
    /** counted plus sends not yet confirmed. */
    projected: number;
    remaining: number | null;
    percentUsed: number | null;
    exceeded: boolean;
}

/**
 * Spend, rates and limits for one organisation-month.
 *
 * `usage.counted` is confirmed by Meta and is the only figure that may be shown
 * as spend. `usage.pending` is our own optimistic estimate of sends not yet
 * confirmed; show it separately and hedged, never added into the headline.
 */
export interface ServiceCostSummary {
    period: string;
    periodStartMs: number;
    periodEndMs: number;

    currency: string;
    currencySymbol: string;
    market: string;
    marketLabel: string;
    rateSource: 'override' | 'country' | 'default';
    rateCardVersion: string;
    rates: Record<string, number>;

    upcomingRates: Record<string, number> | null;
    upcomingRateCardVersion: string | null;
    upcomingEffectiveFromMs: number | null;

    serviceBillable: boolean;
    serviceBillableFromMs: number;
    serviceBillableFromIso: string;

    usage: {
        counted: Record<string, number>;
        pending: Record<string, number>;
        source: 'counter' | 'estimate';
    };

    spend: {
        byCategory: Record<string, number>;
        total: number;
    };

    limits: Record<string, CategoryLimitState>;

    usageDocPath: string;
    /** Rates exclude Meta's volume-tier discounts, so costs are an upper bound. */
    listRatesOnly: boolean;
}

/** How long a cost summary is reused before refetching. */
const COST_SUMMARY_TTL_MS = 10 * 60 * 1000;

/**
 * The billing currencies Meta publishes a rate card in.
 *
 * This is metadata only — a currency appears in the picker solely when
 * `whatsapp_pricing/{CODE}` actually exists (see `availableCurrencyOptions`).
 * Listing a currency here with no seeded document would render an empty rate
 * table rather than an error.
 *
 * South Africa is not on this list and does not need to be: `ZA` is a standalone
 * *market* on the USD card. ZAR is not a Meta billing currency, so South African
 * WABAs are invoiced in USD.
 */
export const META_BILLING_CURRENCIES: { code: string; name: string; symbol: string }[] = [
    { code: 'AED', name: 'UAE Dirham', symbol: 'د.إ' },
    { code: 'ARS', name: 'Argentine Peso', symbol: '$' },
    { code: 'AUD', name: 'Australian Dollar', symbol: 'A$' },
    { code: 'BRL', name: 'Brazilian Real', symbol: 'R$' },
    { code: 'CLP', name: 'Chilean Peso', symbol: '$' },
    { code: 'COP', name: 'Colombian Peso', symbol: '$' },
    { code: 'EUR', name: 'Euro', symbol: '€' },
    { code: 'GBP', name: 'British Pound Sterling', symbol: '£' },
    { code: 'IDR', name: 'Indonesian Rupiah', symbol: 'Rp' },
    { code: 'INR', name: 'Indian Rupee', symbol: '₹' },
    { code: 'MXN', name: 'Mexican Peso', symbol: 'MX$' },
    { code: 'MYR', name: 'Malaysian Ringgit', symbol: 'RM' },
    { code: 'PEN', name: 'Peruvian Sol', symbol: 'S/' },
    { code: 'SAR', name: 'Saudi Riyal', symbol: '﷼' },
    { code: 'SGD', name: 'Singapore Dollar', symbol: 'S$' },
    { code: 'USD', name: 'US Dollar', symbol: '$' }
];

@Injectable({
    providedIn: 'root'
})
export class WhatsAppPricingService {

    private firestore = inject(Firestore);
    private functions = inject(Functions);
    private injector = inject(EnvironmentInjector);
    private readonly saveMessagingLimitsFn = httpsCallable(this.functions, 'wabSaveMessagingLimits');
    private readonly getMessagingUsageFn = httpsCallable(this.functions, 'wabGetMessagingUsage');
    private readonly getServiceCostSummaryFn = httpsCallable(this.functions, 'wabGetServiceCostSummary');
    private costSummaryCache: Record<string, { value$: Observable<ServiceCostSummary | null>; fetchedAt: number }> = {};

    /**
     * Get pricing rates for a specific billing currency
     */
    getPricingRates(currency: string, atMs: number = Date.now()): Observable<WhatsAppPricingDocument | null> {
        const docRef = doc(this.firestore, 'whatsapp_pricing', currency);
        return from(runInInjectionContext(this.injector, () => getDoc(docRef))).pipe(
            map(docSnap => {
                if (!docSnap.exists()) return null;

                const data = docSnap.data() as any;
                const revisions = (data.revisions || []) as WhatsAppRateRevision[];

                // Pick by date rather than trusting the stored `rates` field.
                // That field is a convenience mirror written at seed time; on
                // 1 October the October revision must take over on its own,
                // without anyone re-seeding or deploying.
                const live = pickEffective(revisions, atMs);
                const next = pickUpcoming(revisions, atMs);

                return {
                    currency,
                    ...data,
                    rates: live ? live.rates : (data.rates || []),
                    rateCardVersion: live ? live.rateCardVersion : data.rateCardVersion,
                    effectiveFromMs: live ? live.effectiveFromMs : data.effectiveFromMs,
                    upcoming: next
                } as WhatsAppPricingDocument;
            })
        );
    }

    /**
     * Get all available pricing currencies (documents in the collection)
     */
    getAvailableCurrencies(): Observable<string[]> {
        const colRef = collection(this.firestore, 'whatsapp_pricing');
        return from(runInInjectionContext(this.injector, () => getDocs(colRef))).pipe(
            map(snapshot => snapshot.docs.map(d => d.id))
        );
    }

    /**
     * Get messaging limit configuration for an organization
     */
    getMessagingLimits(organizationId: string): Observable<MessagingLimitConfig | null> {
        const orgRef = doc(this.firestore, 'organizations', organizationId);
        return from(runInInjectionContext(this.injector, () => getDoc(orgRef))).pipe(
            map(docSnap => {
                if (docSnap.exists()) {
                    const data = docSnap.data();
                    return data['messagingLimits'] || null;
                }
                return null;
            })
        );
    }

    /**
     * Save messaging limit configuration via Cloud Function
     */
    saveMessagingLimits(organizationId: string, limits: MessagingLimitConfig): Observable<any> {
        return from(this.saveMessagingLimitsFn({ organizationId, limits })).pipe(
            map((result: any) => result.data)
        );
    }

    /**
     * Get current messaging usage counts for the billing period
     */
    getMessagingUsage(organizationId: string): Observable<any> {
        return from(this.getMessagingUsageFn({ organizationId })).pipe(
            map((result: any) => result.data)
        );
    }

    /**
     * Month-to-date spend, rates and limits for the conversations screen.
     *
     * Cached per (organizationId, period) for ten minutes. This is deliberately
     * NOT called per conversation: it resolves the market and rate card, which
     * change quarterly. Live counts arrive separately through a Firestore
     * listener on the usage document named by `usageDocPath`.
     */
    getServiceCostSummary(organizationId: string, period?: string): Observable<ServiceCostSummary | null> {
        const key = `${organizationId}:${period || 'current'}`;
        const cached = this.costSummaryCache[key];

        if (cached && Date.now() - cached.fetchedAt < COST_SUMMARY_TTL_MS) {
            return cached.value$;
        }

        const value$ = from(this.getServiceCostSummaryFn({ organizationId, period })).pipe(
            map((result: any) => result.data as ServiceCostSummary),
            catchError((err) => {
                // A cost indicator is never worth breaking the inbox over.
                console.error('Could not load the service cost summary:', err);
                return of(null);
            }),
            shareReplay(1)
        );

        this.costSummaryCache[key] = { value$, fetchedAt: Date.now() };
        return value$;
    }

    /** Drop the cache, e.g. after limits are saved. */
    clearCostSummaryCache(): void {
        this.costSummaryCache = {};
    }

    /**
     * What one free-form reply costs right now, in the org's billing currency.
     *
     * Returns 0 before 1 October 2026, when service messages are genuinely free.
     */
    serviceRateNow(summary: ServiceCostSummary | null, atMs: number = Date.now()): number {
        if (!summary) return 0;
        if (!isServiceBillable(atMs)) return 0;
        return summary.rates?.['service'] || 0;
    }

    /**
     * Resolve the rate row an organisation prices against.
     *
     * Meta prices by market, not by country: roughly forty countries have their
     * own row, and everything else resolves through a region. The previous
     * lookup compared `code` to the org's country code and, on no match, fell
     * back to `rates[0]` — an unrelated country's price, shown with no error.
     * Any org in a regional-grouping country (Kenya, Zimbabwe, Namibia…) hit
     * that path every time.
     *
     * Returns `null` rather than an arbitrary row when the card has no usable
     * entry: no price is recoverable, a wrong price is not.
     *
     * @param rates The rate card's rows.
     * @param countryCode The organisation's ISO alpha-2 country.
     * @param marketOverride `organizations/{id}.whatsapp.pricingMarket`, when set.
     */
    resolveOrgRate(
        rates: WhatsAppRate[],
        countryCode?: string,
        marketOverride?: string
    ): { rate: WhatsAppRate | null; market: string; source: 'override' | 'country' | 'default' } {
        const byCode: Record<string, WhatsAppRate> = {};
        for (const rate of rates) {
            byCode[rate.code] = rate;
        }

        if (marketOverride) {
            const override = byCode[marketOverride.trim().toUpperCase()];
            if (override) {
                return { rate: override, market: override.code, source: 'override' };
            }
        }

        const market = marketForCountry(countryCode);
        const matched = byCode[market];
        if (matched) {
            return { rate: matched, market, source: 'country' };
        }

        return { rate: byCode['OTHER'] || null, market: 'OTHER', source: 'default' };
    }

    /**
     * Calculate estimated cost for a given volume of messages
     */
    calculateCost(
        rates: WhatsAppRate,
        volumes: { marketing: number; utility: number; authentication: number; service: number }
    ): { marketing: number; utility: number; authentication: number; service: number; total: number } {
        const marketing = volumes.marketing * (rates.marketing || 0);
        const utility = volumes.utility * (rates.utility || 0);
        const authentication = volumes.authentication * (rates.authentication || 0);
        const service = volumes.service * (rates.service || 0);
        return {
            marketing,
            utility,
            authentication,
            service,
            total: marketing + utility + authentication + service
        };
    }
}
